import { Branding } from "@/components/OfferPage/Branding";
import { Site } from "@/types/sites";
import type { GetServerSideProps } from "next";
import { getAppProps } from "src/utils/globalServerData";
import commonStyles from "src/styles/Common.module.css";
import pageStyles from "./Policy.module.css";
import { PrivacyPageResponse } from "@/types/privacy";
import { TermsAndConditionsPageResponse } from "@/types/terms";
import { getPrivacyPage } from "src/api/privacy/index";
import { getTermsAndConditionsPage } from "src/api/terms/index";
import { generateRandomCookieValue } from "src/utils/cookies/shared";
import { visitReport } from "src/api/report/visit";
import { PageProps } from "@/types/nextjs";
import { UnsubscribePageResponse } from "@/types/unsubscribe";
import { getUnsubscribePage } from "../../api/unsubscribe";
import { exportServerTimings } from "@/utils/serverTiming";

type PolicyPage = ({
    sanitisedPolicyText,
    site,
}: {
    sanitisedPolicyText: string;
    site: Site;
}) => React.ReactElement;

type Getters = ({ policyType }: { policyType?: string | string[] }) => {
    apiGetter: (
        site: Site | null,
        region?: string
    ) => Promise<
        | PrivacyPageResponse
        | TermsAndConditionsPageResponse
        | UnsubscribePageResponse
        | null
    >;
    propertyGetter: (
        entity:
            | PrivacyPageResponse
            | TermsAndConditionsPageResponse
            | UnsubscribePageResponse
            | null
    ) => string;
};

const PolicyPage: PolicyPage = ({ sanitisedPolicyText, site }) => {
    return (
        <div className={commonStyles.container}>
            <Branding site={site} />
            <div className={commonStyles.pageContents}>
                <div
                    className={pageStyles.policyPage}
                    dangerouslySetInnerHTML={{
                        __html: sanitisedPolicyText,
                    }}
                />
            </div>
        </div>
    );
};

const UNSUBSCRIBE_REDIRECT_URL = "https://www.qihmarketing.com/unsub/";

enum PolicyType {
    UNSUBSCRIBE = "unsubscribe",
    TERMS_AND_CONDITIONS = "terms-and-conditions",
    PRIVACY = "privacy",
}

const getPolicyGetters: Getters = ({ policyType }) => {
    const propertyGetter: (
        entity:
            | PrivacyPageResponse
            | TermsAndConditionsPageResponse
            | UnsubscribePageResponse
            | null
    ) => string = (entity) => {
        const attributes = entity?.data?.attributes;
        if (attributes && "sanitisedprivacyPolicyText" in attributes) {
            return attributes.sanitisedprivacyPolicyText ?? "";
        } else if (
            attributes &&
            "sanitisedTermsAndConditionsText" in attributes
        ) {
            return attributes.sanitisedTermsAndConditionsText ?? "";
        } else if (attributes && "sanitisedUnsubscribeText" in attributes) {
            return attributes.sanitisedUnsubscribeText ?? "";
        } else {
            return "";
        }
    };

    let apiGetter: (
        site: Site | null,
        region?: string
    ) => Promise<
        | PrivacyPageResponse
        | TermsAndConditionsPageResponse
        | UnsubscribePageResponse
        | null
    > = () => Promise.resolve(null);

    const policySingleType = Array.isArray(policyType)
        ? policyType[0]
        : policyType;
    switch (policySingleType) {
        case PolicyType.PRIVACY:
            apiGetter = getPrivacyPage;
            break;
        case PolicyType.TERMS_AND_CONDITIONS:
            apiGetter = getTermsAndConditionsPage;
            break;
        case PolicyType.UNSUBSCRIBE:
            apiGetter = getUnsubscribePage;
            break;
    }

    return { apiGetter, propertyGetter };
};

export const getServerSideProps: GetServerSideProps<Omit<PageProps, "pageData">> = async (
    context
) => {
    const visitId = generateRandomCookieValue();
    const appPropsPromise = getAppProps({ context, visitId });
    const { apiGetter, propertyGetter } = getPolicyGetters({
        policyType: context?.params?.policyType,
    });

    // Get the region from the query parameters
    const region = Array.isArray(context.query.region)
        ? context.query.region.at(-1)
        : context.query.region;

    const [{ site, siteTheme }] = await Promise.all([appPropsPromise]);

    const page = await apiGetter(site, region);

    const visitReportPromise = visitReport({
        context,
        visitId,
        site,
        policyPage: page,
    });

    const sanitisedPolicyText = propertyGetter(page);

    // When this feature is first rolled out, there will be no content in the CMS
    // To prevent a 404, we will redirect to the current unsubscribe page until the content is
    // added in the CMS
    if (
        context?.params?.policyType === PolicyType.UNSUBSCRIBE &&
        !sanitisedPolicyText
    ) {
        return {
            redirect: {
                destination: UNSUBSCRIBE_REDIRECT_URL,
                permanent: false,
            },
        };
    }

    await visitReportPromise;

    context.res.setHeader('Server-Timing', exportServerTimings());
    

    return {
        props: { sanitisedPolicyText, site, siteTheme },
        notFound: !(site && sanitisedPolicyText),
    };
};

export default PolicyPage;
